import React, { useState, useRef, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "./ui/dialog";
import { Card, CardContent } from "./ui/card";
import {
  Upload,
  Image as ImageIcon,
  Trash2,
  Check,
  X,
  Move,
  Save,
  Loader2,
  Plus,
  Settings
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const COMPONENT_COLORS = {
  1: "#3B82F6",
  2: "#10B981", 
  3: "#F59E0B",
  4: "#EF4444",
  5: "#8B5CF6"
};

const ImageLibraryManager = ({ 
  isOpen, 
  onClose, 
  assetType, 
  componentLabels,
  onImageSaved 
}) => {
  const { token } = useAuth();
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imageName, setImageName] = useState("");
  const [markers, setMarkers] = useState([]);
  const [isPlacingMarker, setIsPlacingMarker] = useState(null);
  const imageContainerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen, assetType]);

  const fetchImages = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/asset-images/${assetType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
      
      // Load active image for editing
      const activeImage = response.data.find(img => img.is_active);
      if (activeImage) {
        setSelectedImage(activeImage);
        setImageUrl(activeImage.image_url);
        setImageName(activeImage.image_name);
        setMarkers(activeImage.markers || []);
      }
    } catch (error) {
      console.error("Failed to fetch images:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (e) => {
    if (!isPlacingMarker || !imageContainerRef.current) return;
    
    // Get the actual image element
    const img = imageContainerRef.current.querySelector('img');
    if (!img) return;
    
    // Get the image's actual rendered dimensions and position
    const imgRect = img.getBoundingClientRect();
    
    // Calculate click position relative to the image (not the container)
    const x = ((e.clientX - imgRect.left) / imgRect.width) * 100;
    const y = ((e.clientY - imgRect.top) / imgRect.height) * 100;
    
    // Ensure coordinates are within bounds (0-100)
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    // Update or add marker
    const existingIndex = markers.findIndex(m => m.component_id === isPlacingMarker);
    if (existingIndex >= 0) {
      const newMarkers = [...markers];
      newMarkers[existingIndex] = { component_id: isPlacingMarker, x: clampedX, y: clampedY };
      setMarkers(newMarkers);
    } else {
      setMarkers([...markers, { component_id: isPlacingMarker, x: clampedX, y: clampedY }]);
    }
    
    setIsPlacingMarker(null);
  };

  const handleSaveImage = async () => {
    if (!imageUrl) {
      toast.error("Please enter an image URL");
      return;
    }
    
    try {
      setIsSaving(true);
      
      const payload = {
        asset_type: assetType,
        image_url: imageUrl,
        image_name: imageName || `${assetType} Image`,
        markers: markers
      };
      
      let response;
      if (selectedImage) {
        // Update existing
        response = await axios.patch(
          `${API}/asset-images/${selectedImage.id}`,
          { image_url: imageUrl, image_name: imageName, markers: markers, is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new
        response = await axios.post(
          `${API}/asset-images`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success("Image saved successfully");
      onImageSaved?.(response.data);
      fetchImages();
    } catch (error) {
      toast.error("Failed to save image");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axios.delete(`${API}/asset-images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Image deleted");
      fetchImages();
      if (selectedImage?.id === imageId) {
        setSelectedImage(null);
        setImageUrl("");
        setImageName("");
        setMarkers([]);
      }
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleSelectExisting = (image) => {
    setSelectedImage(image);
    setImageUrl(image.image_url);
    setImageName(image.image_name);
    setMarkers(image.markers || []);
  };

  const handleNewImage = () => {
    setSelectedImage(null);
    setImageUrl("");
    setImageName("");
    setMarkers([]);
  };

  const removeMarker = (componentId) => {
    setMarkers(markers.filter(m => m.component_id !== componentId));
  };

  const getMarkerPosition = (componentId) => {
    return markers.find(m => m.component_id === componentId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="image-library-modal" onClose={onClose}>
        <DialogHeader>
          <DialogTitle className="text-heading text-2xl flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-blue-600" />
            Image Library - {assetType.charAt(0).toUpperCase() + assetType.slice(1)}
          </DialogTitle>
          <DialogDescription>
            Upload your own images and position the component markers. Click on the image to place markers.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-6 mt-4">
          {/* Left Panel - Image List */}
          <div className="col-span-3 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Saved Images</h3>
              <Button size="sm" variant="outline" onClick={handleNewImage}>
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : images.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No images saved yet</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {images.map((image) => (
                  <Card 
                    key={image.id}
                    className={`cursor-pointer transition-all ${
                      selectedImage?.id === image.id ? 'ring-2 ring-blue-500' : ''
                    } ${image.is_active ? 'border-green-500' : ''}`}
                    onClick={() => handleSelectExisting(image)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{image.image_name}</p>
                          <p className="text-xs text-slate-500">
                            {image.markers?.length || 0} markers
                            {image.is_active && (
                              <span className="ml-2 text-green-600">• Active</span>
                            )}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Center - Image Editor */}
          <div className="col-span-6 space-y-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="imageUrl">Image</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="imageUrl"
                    placeholder="https://example.com/transformer-diagram.png"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className="flex-1"
                    data-testid="image-url-input"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const response = await axios.post(`${API}/upload`, formData, {
                            headers: { 
                              Authorization: `Bearer ${token}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          });
                          
                          // Construct the full URL for display
                          const uploadedUrl = `${API.replace('/api', '')}${response.data.url}`;
                          setImageUrl(uploadedUrl);
                          if (!imageName) {
                            setImageName(file.name.replace(/\.[^/.]+$/, ""));
                          }
                          toast.success("Image uploaded successfully");
                        } catch (error) {
                          toast.error("Failed to upload image");
                          console.error(error);
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Browse
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">Enter a URL or upload an image from your computer</p>
              </div>
              <div>
                <Label htmlFor="imageName">Image Name</Label>
                <Input
                  id="imageName"
                  placeholder="Transformer Diagram - Front View"
                  value={imageName}
                  onChange={(e) => setImageName(e.target.value)}
                  className="mt-1"
                  data-testid="image-name-input"
                />
              </div>
            </div>

            {/* Image Preview with Markers */}
            <div 
              ref={imageContainerRef}
              className={`relative bg-slate-100 rounded-lg border-2 flex items-center justify-center ${
                isPlacingMarker ? 'border-blue-500 cursor-crosshair' : 'border-slate-200'
              }`}
              style={{ minHeight: "350px" }}
              onClick={handleImageClick}
              data-testid="image-preview-container"
            >
              {imageUrl ? (
                <div className="relative inline-block">
                  <img 
                    src={imageUrl} 
                    alt="Asset Preview"
                    className="max-w-full rounded-lg"
                    style={{ maxHeight: "350px" }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  
                  {/* Render Markers - positioned relative to image */}
                  {markers.map((marker) => {
                    const componentNum = parseInt(marker.component_id);
                    return (
                      <div
                        key={marker.component_id}
                        className="absolute w-10 h-10 flex items-center justify-center rounded-full text-white font-bold text-lg shadow-lg transition-transform hover:scale-110"
                        style={{
                          left: `${marker.x}%`,
                          top: `${marker.y}%`,
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: COMPONENT_COLORS[componentNum] || "#6B7280",
                          border: "3px solid white",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                        }}
                      >
                        {componentNum}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                  <Upload className="w-12 h-12 mb-2" />
                  <p className="text-sm">Enter an image URL above to preview</p>
                </div>
              )}
              
              {isPlacingMarker && (
                <div className="absolute top-2 left-2 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                  Click to place marker #{isPlacingMarker}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Marker Controls */}
          <div className="col-span-3 space-y-4">
            <h3 className="font-semibold text-slate-900">Component Markers</h3>
            <p className="text-xs text-slate-500">
              Click a component below, then click on the image to place its marker.
            </p>
            
            <div className="space-y-2">
              {componentLabels.map((label, idx) => {
                const componentId = String(idx + 1);
                const markerPos = getMarkerPosition(componentId);
                const isPlacing = isPlacingMarker === componentId;
                
                return (
                  <div 
                    key={componentId}
                    className={`p-3 rounded-md border transition-all ${
                      isPlacing 
                        ? 'border-blue-500 bg-blue-50' 
                        : markerPos 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: COMPONENT_COLORS[idx + 1] }}
                        >
                          {idx + 1}
                        </div>
                        <span className="text-sm font-medium">{label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {markerPos && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500"
                            onClick={() => removeMarker(componentId)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={isPlacing ? "default" : "outline"}
                          className={`h-7 ${isPlacing ? 'bg-blue-600' : ''}`}
                          onClick={() => setIsPlacingMarker(isPlacing ? null : componentId)}
                        >
                          {isPlacing ? (
                            <X className="w-4 h-4" />
                          ) : markerPos ? (
                            <Move className="w-4 h-4" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    {markerPos && (
                      <p className="text-xs text-slate-400 mt-1 ml-8">
                        Position: {markerPos.x.toFixed(1)}%, {markerPos.y.toFixed(1)}%
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleSaveImage}
              disabled={!imageUrl || isSaving}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="save-image-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save & Set Active
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLibraryManager;
